import { defineComponent as _defineComponent } from 'vue'
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderStreamsComparatorBar from './ProviderStreamsComparatorBar.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { StreamInfo, CompetitionContent } from '@/features/sports/types/provider-streams-comparator-types';
import { Platform, SportEventAggregation } from '@/@types/graphql-types';
import {
	GetCompetitionOffersDocument,
	GetCompetitionOffersQueryVariables,
	GetCompetitionOffersQuery,
} from '@/features/sports/graphql/queries/GetCompetitionOffers.query';

import { contextIs, SingleStepContext } from '@/features/sports/helpers/config';
import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { usePersistedTodaysDate } from '@/helpers/composables/usePersistedTodaysDate';
import { isMultiStepSport } from '@/features/sports/types/enums';
import { useQuery } from '@/helpers/composables/useApollo';
import { computed, inject, ref } from 'vue';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { SPORT_PROVIDERS_BY_SHORT_NAME_KEY } from '@/features/sports/types/sports-types';
import { useI18n } from '@/helpers/composables/useI18n';
import { useRoute } from 'vue-router/composables';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProviderStreamsComparator',
  props: {
    context: null,
    competitionId: null
  },
  setup(__props: any) {

const props = __props as {
	context: SingleStepContext /* Data Fetching */;
	competitionId?: string; // Required on competition pages
};



const providers = inject(SPORT_PROVIDERS_BY_SHORT_NAME_KEY);
const { todayDate } = usePersistedTodaysDate();
const { country, language, localeObject } = useLanguageStore();
const { t } = useI18n();
const route = useRoute();

const comparatorDays = computed(() => (isMultiStepSport(props.context.sportType) ? 14 : 7));

const queryVariables = computed<GetCompetitionOffersQueryVariables>(() => {
	const variables = {
		country: country.value,
		language: language.value,
		platform: Platform.Web,
		timezoneOffset: 0,
		filter: {
			startAt: {
				from: todayDate.value.toISOString(),
				to: shiftDateWithTimezone(
					localeObject.value.timezone,
					comparatorDays.value,
					todayDate.value
				).toISOString(),
			},
			packages: providers?.value?.map(({ shortName }) => shortName),
			sports: [props.context.sportType].flat(),
			competitionIds: [] as string[],
		},
		groupBy: SportEventAggregation.Competition,
		first: 7, // number of competitions to get (ideally it would be all the competitions in 7 days, max: ~65)
		bucketSize: 15, // number of offers per event, ideally should match the bucketSize in the competition page query
	};

	if (route.name === 'app.sports.competition' && contextIs.competition(props.context)) {
		if (props.competitionId == null) {
			console.warn('[ProviderStreamsComparator]: Missing required prop: "competitionId"');
		} else {
			variables.filter.competitionIds = [props.competitionId];
		}
	}

	return variables;
});

const { result } = useQuery<GetCompetitionOffersQuery, GetCompetitionOffersQueryVariables>(
	GetCompetitionOffersDocument,
	queryVariables
);

const competitionOffers = computed(() => {
	return result.value;
});

const providerStreamsInfo = computed(() => {
	const competitions = competitionOffers.value?.sportEventBucketsV2.edges;
	if (competitions == null) return [];

	const result = {} as { [shortName: string]: StreamInfo };
	providers?.value?.forEach(provider => (result[provider.shortName] = { provider, totalCount: 0, competitions: {} }));

	competitions.forEach(({ key, node }) => {
		const events = node.edges;
		if (events == null || events.length === 0) return;

		const { name = '', iconUrl, iconBlurHash, fullPath } = key.competition?.content as CompetitionContent;
		const competitionData = { name, iconUrl, iconBlurHash, fullPath, count: 0 };

		events.forEach(event => {
			// same event might have multiple offers for whatever reason (highlight, main event, audio languages, etc.)
			const offerProviders = new Set(event.node.offers.map(offer => offer.package.shortName));
			offerProviders.forEach(shortName => {
				if (result[shortName] == null) return;

				const competitionItem = result[shortName].competitions;
				if (competitionItem[name] == null) {
					competitionItem[name] = { ...competitionData };
				}

				competitionItem[name].count++;
				result[shortName].totalCount++;
			});
		});
	});
	const data = Object.values(result)
		.filter(({ totalCount }) => totalCount > 0)
		.sort(({ totalCount: a }, { totalCount: b }) => b - a)
		.slice(0, 10);

	selectedProvider.value = data[0]?.provider?.shortName ?? '';
	return data;
});

const selectedProvider = ref('');

const selectedProviderStreamInfo = computed(() => {
	return providerStreamsInfo.value.find(({ provider }) => provider.shortName === selectedProvider.value);
});

const eventTotal = computed(() => {
	return providerStreamsInfo.value.reduce((total, info) => (total += info.totalCount), 0);
});

const topProvider = computed(() => {
	return {
		total: providerStreamsInfo.value[0]?.totalCount ?? 0,
		name: providerStreamsInfo.value[0]?.provider?.clearName ?? '',
	};
});

const providerList = computed(() => {
	const sorted = providerStreamsInfo.value.slice().sort((a, b) => {
		return b.totalCount - a.totalCount;
	});
	return sorted.map((provider: any) => {
		return `${provider.provider.clearName} (${provider.totalCount})`;
	});
});

const formattedProviderList = computed(() => {
	if (providerList.value.length > 1) {
		const list = providerList.value;
		const last = list[list.length - 1];
		return `${list.slice(0, -1).join(', ')} and ${last}`;
	} else {
		return providerList.value;
	}
});
// TRANSLATIONS
const eventTranslation = computed(() => {
	if (
		isMultiStepSport(props.context.sportType) ||
		props.context.sportName === 'SPORTS_WEBAPP_SPORT_TYPE_MOTORSPORT'
	) {
		return (count: number) => t('WEBAPP_GLOBAL_RACES', { count });
	}

	return (count: number) => t('WEBAPP_GLOBAL_MATCHES', { count });
});

return { __sfc: true,props, providers, todayDate, country, language, localeObject, t, route, comparatorDays, queryVariables, result, competitionOffers, providerStreamsInfo, selectedProvider, selectedProviderStreamInfo, eventTotal, topProvider, providerList, formattedProviderList, eventTranslation, HiddenHorizontalScrollbar, ProviderStreamsComparatorBar, ProviderIcon }
}

})